/**
 * Layout pieces shared by the Phase 2 marketing pages (homepage v2, core
 * feature pages, property managers, research, all features).
 */
import type { ReactNode } from "react";
import { Link } from "wouter";
import { Check } from "lucide-react";
import { StartFreeButton, TourButton, TrustLine } from "@/components/marketing/cta";
import { QUOTE_ATTRIBUTION, type CoreBlock } from "@/data/core-pages";
import { TRIAL_DAYS } from "@/lib/marketing/pricing-copy";

export function PageHero({
  eyebrow,
  title,
  sub,
  children,
  ctaLocation,
  showButtons = true,
  testId = "section-hero",
}: {
  eyebrow?: string;
  title: string;
  sub?: ReactNode;
  children?: ReactNode;
  ctaLocation: string;
  showButtons?: boolean;
  testId?: string;
}) {
  return (
    <div className="bg-gradient-to-br from-[#0a1628] via-[#0d2447] to-brand">
      <section className="px-4 pb-14 pt-28 md:pb-20 md:pt-36" data-testid={testId}>
        <div className="mx-auto max-w-4xl text-center">
          {eyebrow && (
            <p className="text-sm font-semibold uppercase tracking-wider text-brand-accent" data-testid="text-eyebrow">
              {eyebrow}
            </p>
          )}
          <h1
            className="mt-4 text-4xl font-bold leading-[1.1] tracking-tight text-white md:text-5xl lg:text-6xl"
            data-testid="text-page-heading"
          >
            {title}
          </h1>
          {sub && (
            <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-blue-100 md:text-xl" data-testid="text-page-sub">
              {sub}
            </p>
          )}
          {children}
          {showButtons && (
            <>
              <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
                <StartFreeButton ctaId={`${ctaLocation}-start-free`} location={ctaLocation} className="w-full px-8 sm:w-auto" />
                <TourButton ctaId={`${ctaLocation}-tour`} location={ctaLocation} className="w-full px-8 sm:w-auto" />
              </div>
              <TrustLine className="mt-4" />
            </>
          )}
        </div>
      </section>
    </div>
  );
}

export function FeatureBlocks({ blocks }: { blocks: Array<CoreBlock & { href?: string }> }) {
  return (
    <div className="mx-auto grid max-w-5xl gap-6 sm:grid-cols-2" data-testid="list-feature-blocks">
      {blocks.map((block) => (
        <div key={block.title} className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          <h2 className="flex items-start gap-3 text-lg font-semibold text-gray-900">
            <Check size={20} className="mt-0.5 shrink-0 text-emerald-600" aria-hidden="true" />
            {block.href ? (
              <Link href={block.href} className="hover:text-brand hover:underline">
                {block.title}
              </Link>
            ) : (
              block.title
            )}
          </h2>
          {block.body && <p className="mt-2 pl-8 text-gray-700">{block.body}</p>}
        </div>
      ))}
    </div>
  );
}

export function FounderQuote({ quote }: { quote: string }) {
  return (
    <figure className="mx-auto max-w-3xl text-center" data-testid="block-quote">
      <blockquote className="text-xl italic leading-relaxed text-gray-800 md:text-2xl">“{quote}”</blockquote>
      <figcaption className="mt-4 text-sm text-gray-600">{QUOTE_ATTRIBUTION}</figcaption>
    </figure>
  );
}

/** A1 section 7 final band, reused at the bottom of the core pages. */
export function FinalBand({ location }: { location: string }) {
  return (
    <section className="bg-gradient-to-br from-[#0a1628] to-[#0d2447] px-4 py-16 md:py-20" data-testid="section-final-band">
      <div className="mx-auto max-w-3xl text-center">
        <h2 className="text-3xl font-bold text-white md:text-4xl">Try PadPulse on your own houses.</h2>
        <p className="mt-3 text-lg text-blue-100">
          {TRIAL_DAYS} days free. No card. About 3 minutes to connect PadSplit.
        </p>
        <div className="mt-8 flex justify-center">
          <StartFreeButton ctaId={`${location}-final-start-free`} location={`${location}_final_band`} className="px-10" />
        </div>
      </div>
    </section>
  );
}

export function FaqList({ items, testId = "section-faq" }: { items: Array<{ question: string; answer: string }>; testId?: string }) {
  return (
    <dl className="space-y-6" data-testid={testId}>
      {items.map((item) => (
        <div key={item.question} className="border-b border-gray-100 pb-6">
          <dt className="text-lg font-semibold text-gray-900">{item.question}</dt>
          <dd className="mt-2 text-gray-700">{item.answer}</dd>
        </div>
      ))}
    </dl>
  );
}
